import React, { useEffect, useState } from "react";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { Skeleton } from "@mui/material";
import Text from "../../styleguide/Text";
import { KTSVG } from "../../helpers/KTSVG";

function not(a: string[], b: string[]) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: string[], b: string[]) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

const CustomList = ({
  title,
  items,
  listCrudItems,
  listTitleParser,
  isLoading,
  checked,
  handleToggle,
  disabled,
}: {
  title: React.ReactNode;
  items: string[];
  listCrudItems?: any[] | null;
  listTitleParser?: (
    listItem: string,
    crudListItem?: any[],
  ) => string | JSX.Element | JSX.Element[] | React.ReactNode;
  checked: string[];
  isLoading?: boolean;
  handleToggle: (value: string) => void;
  disabled?: boolean;
}) => {
  const [searchList, setSearchList] = useState("");

  const searchedList = items.filter((item) => {
    return item.toLowerCase().includes(searchList.toLowerCase());
  });

  return (
    <Card className="overflow-hidden rounded-card border border-gray-200 shadow-none">
      <CardHeader
        className="w-full bg-surface-subtle p-4 text-center font-semibold text-slate-800"
        sx={{ px: 2, py: 1, minHeight: 52 }}
        titleTypographyProps={{ fontSize: "1rem", fontWeight: 600 }}
        title={title}
      />
      <SearchComponent searchList={searchList} setSearchList={setSearchList} />

      {/* <Divider /> */}
      {isLoading ? (
        <Skeleton variant="rectangular" height={300} />
      ) : (
        <ItemsList
          items={searchedList}
          listCrudItems={listCrudItems}
          checked={checked}
          handleToggle={handleToggle}
          listTitleParser={listTitleParser}
          disabled={disabled}
        />
      )}
    </Card>
  );
};

const SearchComponent = ({ searchList, setSearchList }: any) => {
  return (
    <div className="w-full bg-surface-subtle p-3 text-muted">
      <div className="relative flex items-center">
        <KTSVG
          path="/media/icons/duotune/general/gen021.svg"
          className="absolute left-3 z-10 [&_path]:fill-muted [&_rect]:fill-muted"
          svgClassName="h-5 w-5"
        />
        <input
          type="text"
          data-kt-user-table-filter="search"
          className="h-[32px] w-full rounded-control border border-gray-200 bg-white py-0 pl-10 pr-3 text-sm font-medium text-gray-900 outline-none transition focus:border-accent/40 focus:ring-2 focus:ring-accent/15"
          placeholder="Cauta..."
          value={searchList}
          onChange={(e) => setSearchList(e.target.value)}
        />
      </div>
    </div>
  );
};

const ItemsList = ({
  items,
  listCrudItems,
  checked,
  handleToggle,
  disabled,
  listTitleParser,
}: {
  items: string[];
  listCrudItems?: any[] | null;
  checked: string[];
  handleToggle: any;
  disabled?: boolean;
  listTitleParser?: (
    listItem: string,
    crudListItem?: any[],
  ) => string | JSX.Element | JSX.Element[] | React.ReactNode;
}) => {
  if (items.length === 0) {
    return (
      <div className="flex h-[65vh] items-center justify-center py-3">
        <Text textAlign={"center"}>Lista este goala</Text>
      </div>
    );
  }

  const listTitle = (item: string) => {
    if (listTitleParser && listCrudItems) {
      return listTitleParser(item, listCrudItems);
    }
    return item;
  };
  return (
    <List
      sx={{
        width: "100%",
        height: "65vh",
        bgcolor: "background.paper",
        overflow: "auto",
      }}
      dense
      component="div"
      role="list">
      {items.map((value: string) => {
        const labelId = `transfer-invoice-item-${value}-label`;

        return (
          <ListItem
            key={value}
            role="listitem"
            button
            disabled={disabled}
            onClick={handleToggle(value)}>
            <ListItemIcon>
              <Checkbox
                checked={checked.indexOf(value) !== -1}
                tabIndex={-1}
                disableRipple
                disabled={disabled}
                inputProps={{
                  "aria-labelledby": labelId,
                }}
              />
            </ListItemIcon>
            <ListItemText id={labelId} primary={listTitle(value)} />
          </ListItem>
        );
      })}
      <ListItem />
    </List>
  );
};

export default function TransferList({
  leftListTitle = "",
  rightListTitle = "",
  leftListItems,
  rightListItems,
  setRightList,
  setLeftList,
  isLoading,
  disabled,
  listCrudItems,
  listTitleParser,
  alreadyCheckedItems = [],
  showLeftListTotal = true,
  showRightListTotal = true,
}: {
  leftListTitle?: string;
  rightListTitle?: string;
  leftListItems: string[];
  rightListItems: string[];
  setRightList: (value: string[]) => void;
  setLeftList: (value: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  listCrudItems?: any[] | null;
  listTitleParser?: (
    listItem: string,
    crudListItem?: any[],
  ) => string | JSX.Element | JSX.Element[] | React.ReactNode;
  alreadyCheckedItems?: string[];
  showLeftListTotal?: boolean;
  showRightListTotal?: boolean;
}) {
  const [checked, setChecked] = useState<string[]>([]);

  const leftChecked = intersection(checked, leftListItems);
  const rightChecked = intersection(checked, rightListItems);

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleCheckedRight = () => {
    setRightList(rightListItems.concat(leftChecked));
    setLeftList(not(leftListItems, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeftList(leftListItems.concat(rightChecked));
    setRightList(not(rightListItems, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  useEffect(() => {
    if (alreadyCheckedItems?.length > 0) {
      setChecked(alreadyCheckedItems);
    }
  }, [alreadyCheckedItems]);

  return (
    <div className="flex h-full items-center justify-between gap-6 max-lg:flex-col">
      <div className="w-full min-w-0">
        <CustomList
          title={`${leftListTitle} 
          ${showLeftListTotal ? `(${leftListItems.length})` : ""}`}
          items={leftListItems}
          listCrudItems={listCrudItems}
          listTitleParser={listTitleParser}
          isLoading={isLoading}
          checked={checked}
          handleToggle={handleToggle}
          disabled={disabled}
        />
      </div>
      <div className="shrink-0 max-lg:rotate-90">
        <div className="flex flex-col">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0 || disabled}
            aria-label="move selected right">
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0 || disabled}
            aria-label="move selected left">
            &lt;
          </Button>
        </div>
      </div>
      <div className="w-full min-w-0">
        <CustomList
          title={`${rightListTitle} 
            ${showRightListTotal ? `(${rightListItems.length})` : ""}`}
          items={rightListItems}
          listCrudItems={listCrudItems}
          listTitleParser={listTitleParser}
          isLoading={isLoading}
          checked={checked}
          handleToggle={handleToggle}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
